import React, { useEffect, useState } from "react";
import { getCertificationContext, resubmitSample } from "../../api/client";

/** A sample the Control Authority returned for correction: why, and the
 *  re-certification that sends it back for review (40 CFR 403.12(l)). */
export default function ReturnedSampleBox({ sample, companyId, onResubmitted }: {
  sample: { id: number; review_comment?: string | null; reviewed_at?: string | null };
  companyId?: number | null;
  onResubmitted: () => void;
}) {
  const [cert, setCert] = useState<{ statement_text: string; can_sign: boolean;
                                     authority_name: string | null } | null>(null);
  const [agreed, setAgreed]     = useState(false);
  const [name, setName]         = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");

  useEffect(() => {
    getCertificationContext(companyId ?? undefined).then(r => setCert(r.data)).catch(() => {});
  }, [companyId]);

  const resubmit = async () => {
    setSaving(true); setError("");
    try {
      await resubmitSample(sample.id, { signer_name: name, password });
      setPassword("");
      onResubmitted();
    } catch (e: any) {
      setError(e.response?.data?.error ?? "Could not resubmit the sample.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div role="alert" style={s.box}>
      <div style={s.head}>Returned for correction{sample.reviewed_at ? ` on ${sample.reviewed_at.slice(0, 10)}` : ""}</div>
      <div style={s.reason}>{sample.review_comment || "Your coordinator returned this sample for correction."}</div>
      <div style={s.help}>
        Correct the sample below (Edit Header / Edit Results), then re-certify it to send it back for review.
        The original submission stays on record.
      </div>

      {cert && !cert.can_sign ? (
        <div style={s.note}>
          Only the company's designated signature authority
          {cert.authority_name ? <> (<strong>{cert.authority_name}</strong>)</> : null} can re-certify this sample.
        </div>
      ) : (
        <>
          <div style={s.statement}>{cert?.statement_text ?? "I certify under penalty of law that this document and all attachments were prepared under my direction or supervision…"}</div>
          <label style={s.check}>
            <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} />
            I am the authorized representative and I certify the above statement for this corrected sample.
          </label>
          <div style={s.row}>
            <input style={s.input} aria-label="Full legal name" placeholder="Full legal name"
              value={name} onChange={e => setName(e.target.value)} />
            <input style={s.input} type="password" aria-label="Password" placeholder="Re-enter your password"
              value={password} onChange={e => setPassword(e.target.value)} />
            <button style={s.btn} onClick={resubmit}
              disabled={saving || !agreed || !name.trim() || !password}>
              {saving ? "Resubmitting…" : "Re-certify & Resubmit"}
            </button>
          </div>
          {error && <div style={s.error}>{error}</div>}
        </>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  box:       { background:"#fffaf0", border:"1px solid #fbd38d", borderRadius:8, padding:"12px 14px", margin:"4px 0 14px" },
  head:      { fontWeight:700, color:"#c05621", fontSize:14 },
  reason:    { margin:"6px 0", fontSize:14, color:"#2d3748", whiteSpace:"pre-wrap" },
  help:      { fontSize:12.5, color:"#744210", marginBottom:10 },
  note:      { fontSize:13, color:"#7b341e" },
  statement: { fontSize:12, color:"#4a5568", fontStyle:"italic", lineHeight:1.5, marginBottom:8 },
  check:     { display:"flex", gap:8, alignItems:"flex-start", fontSize:13, marginBottom:8 },
  row:       { display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" },
  input:     { flex:"1 1 180px", minWidth:0, padding:"7px 10px", fontSize:13, border:"1px solid #cbd5e0", borderRadius:6 },
  btn:       { padding:"8px 14px", fontSize:13, fontWeight:700, borderRadius:6, border:"none", cursor:"pointer",
               background:"#c05621", color:"#fff" },
  error:     { marginTop:8, fontSize:13, color:"#c53030" },
};
